<template>
  <transition-fade appear @after-enter="determineAutoSize">
    <div
      :key="lyrics"
      ref="container"
      class="karaokePlayer-lyrics leading-snug whitespace-pre overflow-hidden box-border grow shrink-0 font-lyrics font-normal min-h-[calc(100vh_-_20rem)] h-[unset] w-[90%]"
      v-bind="$attrs"
      :style="{
        fontSize: `${autoSize}px`,
      }"
    >
      {{ lyrics }}
    </div>
  </transition-fade>
</template>

<script>
import TransitionFade from "../../../../magic/TransitionFade.vue";

// Note `.min-h-[calc(100vh_-_20rem)]`: Safari workaround for "height: 100% in flex-item's child" bug

export default {
  name: "KaraokeLyrics",
  components: {
    TransitionFade,
  },
  data() {
    return {
      autoSize: undefined,
    };
  },
  props: {
    cuePoints: {
      type: Array,
      default: () => [],
    },
    currentTime: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    lyrics() {
      const currentCuePoint = this.cuePoints
        ?.filter((item) => this.currentTime * 1000 >= item.time)
        .pop();
      return currentCuePoint?.lyrics;
    },
  },
  watch: {
    cuePoints() {
      // Reset the instance data.
      Object.assign(this.$data, this.$options.data.call(this));
    },
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.autoSize = undefined;
      this.determineAutoSize();
    });
  },
  methods: {
    pollContainerExpanded() {
      const container = this.$refs.container;
      if (!container) {
        return Promise.reject();
      }

      // When opening a song in a new tab or reloading a song page,
      // then the container height is `0` for a quick moment.
      // We need to wait for Vue to initialize fully and all classes to be applied.
      // Only once the container height has expanded to its actual `auto` height
      // can we measure the font size.
      return new Promise((resolve) => {
        const interval = window.setInterval(() => {
          const boundingBox = container.getBoundingClientRect();
          if (boundingBox.height > 0) {
            window.clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    },
    async determineAutoSize() {
      // Measure only if necessary.
      // This allows us to link this to the fade transition's @after-enter hook;
      // making sure we are not surprised by a missing `this.$refs.container`
      // due to DOM patching and transition.
      if (this.autoSize !== undefined) {
        return;
      }

      if (!this.cuePoints?.length) {
        return;
      }

      const container = this.$refs.container;
      if (!container) {
        return;
      }

      const initialInnerText = container.innerText;
      container.innerText = "";

      // Fix width and height to the stretched auto-values.
      container.style.width = "";
      container.style.height = "";

      // When opening a song in a new tab or reloading a song page,
      // the lyrics container has an initial height of `0`.
      // Here, we wait for the container to inflate (re-layouting),
      // so we can fix its size final and measure the maximum font size.
      await this.pollContainerExpanded();

      const boundingBox = container.getBoundingClientRect();
      container.style.width = `${boundingBox.width}px`;
      container.style.height = `${boundingBox.height}px`;

      // Temporarily set overflow to scroll to measure the amount of overflow.
      container.style.overflow = "scroll";
      container.style.visibility = "hidden";

      let size = 8;
      for (; size <= container.clientHeight; size++) {
        // Set font size to measure text overflow.
        container.style.fontSize = `${size}px`;

        // Try all of the song's cue points.
        // If none overflow, advance to the next size.
        const overflows = this.cuePoints?.some((cuePoint) => {
          container.innerText = cuePoint?.lyrics;
          return (
            container.scrollWidth > container.offsetWidth ||
            container.scrollHeight > container.offsetHeight
          );
        });

        if (overflows) {
          // We have an overflow.
          break;
        }
      }

      // Set the last size as measured auto size and stop measuring.
      // We subtract 2 instead of 1 to work around inconsistent scaling
      // due to the rounding error involved between the integer values
      // of offsetWidth/Height and scrollWidth/Height versus the double
      // values of the actual bounding box.
      this.autoSize = size - 2;

      // Reset temporary styles.
      container.style.overflow = "hidden";
      container.style.visibility = "visible";
      container.innerText = initialInnerText;
    },
  },
};
</script>
